import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Send } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export function MessagesPanel({ testid = "messages-panel" }) {
  const { user } = useAuth();
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const scrollRef = useRef();

  useEffect(() => {
    api.get("/messages/threads").then((r) => {
      setThreads(r.data);
      if (r.data.length > 0 && !active) setActive(r.data[0]);
    }).catch(() => {});
  // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!active) return;
    api.get(`/messages/${active.user_id}`).then((r) => {
      setMessages(r.data);
      setTimeout(() => scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight), 50);
    }).catch(() => {});
  }, [active]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || !active) return;
    const { data } = await api.post("/messages", { to_user_id: active.user_id, content: text });
    setMessages([...messages, data]);
    setText("");
    setTimeout(() => scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight), 50);
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-px bg-luxe" style={{ background: "var(--border)", height: "70vh" }} data-testid={testid}>
      <div className="bg-surface-1 overflow-y-auto">
        <div className="p-4 overline">Conversations</div>
        {threads.length === 0 && <div className="p-4 text-sm text-secondary-luxe">No conversations yet.</div>}
        {threads.map((t) => (
          <button key={t.user_id}
            onClick={() => setActive(t)}
            data-testid={`thread-${t.user_id}`}
            className={`w-full text-left p-4 border-t border-luxe hover:bg-surface-2 ${active?.user_id === t.user_id ? "bg-surface-2" : ""}`}>
            <div className="font-medium">{t.name}</div>
            <div className="text-xs text-tertiary-luxe truncate mt-1">{t.last_message || "Start a conversation"}</div>
          </button>
        ))}
      </div>
      <div className="bg-surface-1 flex flex-col">
        {active ? (
          <>
            <div className="p-4 border-b border-luxe">
              <div className="font-serif text-2xl">{active.name}</div>
            </div>
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
              {messages.map((m) => (
                <div key={m.message_id} className={`flex ${m.from_user_id === user.user_id ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[75%] px-4 py-2.5 text-sm ${
                    m.from_user_id === user.user_id ? "bg-accent-luxe text-white" : "bg-surface-2 text-primary-luxe"
                  }`} style={{ borderRadius: "2px" }}>
                    {m.content}
                  </div>
                </div>
              ))}
            </div>
            <form onSubmit={send} className="p-3 border-t border-luxe flex gap-2 items-center">
              <input className="input-luxe flex-1" placeholder="Write…" value={text} onChange={(e) => setText(e.target.value)} data-testid="message-input" />
              <button className="btn-primary px-4" data-testid="message-send"><Send size={14} /></button>
            </form>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-secondary-luxe">Select a conversation</div>
        )}
      </div>
    </div>
  );
}
